/**
 * useServers — the organization's servers, each with whether it is online.
 *
 * One app-wide fetch of the org's servers, shared by every consumer, so `refetch()`
 * from any of them refreshes all; each server's update report is merged in from a fetch of its
 * own, which useUpdates refreshes (see serversAtoms). Online state arrives by push over the
 * notifications stream (see serversAtoms), so the offline banner reacts to a media_server coming
 * online without a reload.
 */

import { useAtomValueRawSync, useSetAtom } from 'jotai';

import {
    fleetServersAtom, refreshServersAtom, revalidateServersAtom, serverOnlineIdsAtom, serversAtom,
} from '@/state/api/serversAtoms';
import type { FetchState } from '@/types/common';
import type { FleetServer } from '@/types/servers';

/** The servers plus which of them are reachable. */
interface Servers extends FetchState {
    servers: FleetServer[];
    onlineIds: ReadonlySet<string>;
    onlineCount: number;
    count: number;
    /** A request for the list is in flight, the first or a reload; `loading` is the first only. */
    isFetching: boolean;
    /** Loads the list again, unless a load in flight or settled moments ago will answer. */
    revalidate: () => void;
}

export function useServers(): Servers {
    const { isLoading, isLoaded, error } = useAtomValueRawSync(serversAtom);
    const servers = useAtomValueRawSync(fleetServersAtom);
    const onlineIds = useAtomValueRawSync(serverOnlineIdsAtom);
    const refetch = useSetAtom(refreshServersAtom);
    const revalidate = useSetAtom(revalidateServersAtom);

    return {
        servers,
        onlineIds,
        onlineCount: servers.filter((server) => server.isOnline).length,
        count: servers.length,
        // No list yet — a reload of an already-loaded list keeps its consumers mounted.
        isLoading: isLoading && !isLoaded,
        isFetching: isLoading,
        error,
        refetch,
        revalidate,
    };
}
